import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import sortBy from 'just-sort-by';
import React, { FunctionComponent, useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';

import {
  currentPageIndexGeneralSearchAtom,
  currentPageIndexSpecificSearchAtom,
  isInitializingAtom,
  refinerItemsAtom,
  selectedLocationIdAtom,
  selectedRefinerItemsAtom,
  selectedSiteAtom,
  startRowGeneralSearchAtom,
  startRowSpecificSearchAtom,
} from '../utils/AtomConfig';
import Constants from '../utils/Constants';
import { FilterFlag } from './FilterFlag';

interface IRefinerProps {
  title: string;
  property: string;
}

const useStyles = makeStyles({
  checkbox: {
    display: 'flex',
    alignItems: 'flex-start',
    '& .MuiFormControlLabel-label': {
      marginTop: 8,
    },
  },
});

export const Refiner: FunctionComponent<IRefinerProps> = (
  props: IRefinerProps
) => {
  const classes = useStyles();

  const [selectedRefiners, setSelectedRefiners] = useAtom(
    selectedRefinerItemsAtom
  );
  const selectedLocationId = useAtomValue(selectedLocationIdAtom);
  const isInitializing = useAtomValue(isInitializingAtom);
  const refinerItems = useAtomValue(refinerItemsAtom);
  const selectedSite = useAtomValue(selectedSiteAtom);
  const setCurrentPageIndexSpecificSearch = useSetAtom(
    currentPageIndexSpecificSearchAtom
  );
  const setStartRowSpecificSearch = useSetAtom(startRowSpecificSearchAtom);
  const setCurrentPageIndexGeneralSearch = useSetAtom(
    currentPageIndexGeneralSearchAtom
  );
  const setStartRowGeneralSearch = useSetAtom(startRowGeneralSearchAtom);

  const [isExpanded, setIsExpanded] = useState(true);

  function resetPagination() {
    setCurrentPageIndexSpecificSearch(1);
    setCurrentPageIndexGeneralSearch(1);
    setStartRowSpecificSearch(0);
    setStartRowGeneralSearch(0);
  }

  function updateCheckedItem(value: string, checked: boolean) {
    let updatedSelectedRefiners = [...selectedRefiners];
    if (checked) {
      // Add selected refiner to the array
      updatedSelectedRefiners.push({ property: props.property, value });
    } else {
      // remove selected refiner from the array
      updatedSelectedRefiners = updatedSelectedRefiners.filter(
        (refiner) =>
          refiner.property !== props.property || refiner.value !== value
      );
    }
    resetPagination();
    setSelectedRefiners(updatedSelectedRefiners);
  }

  function isItemChecked(property: string, value: string) {
    return selectedRefiners.some(
      (refiner) => refiner.property === property && refiner.value === value
    );
  }

  const isLocationSelected = Boolean(selectedLocationId);
  const isAnySiteSelected = Boolean(selectedSite);
  const hasSelectedRefiners = Boolean(
    selectedRefiners.filter((refiner) => refiner.property === props.property)
      .length
  );

  const items =
    refinerItems &&
    refinerItems[props.property] &&
    sortBy(refinerItems[props.property], (item) => item.index);

  return (
    <Accordion
      expanded={isExpanded}
      onChange={(_, expanded) => setIsExpanded(expanded)}
      sx={{
        '& span.MuiCheckbox-root': {
          padding: 0,
        },
        '& span.MuiFormControlLabel-label': {
          marginTop: 0,
          marginLeft: 1,
        },
        '& label.MuiFormControlLabel-root': {
          marginBottom: 1
        }
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls='panel-function-content'
        id='panel-function-header'
        {...Constants.BASE_STYLES.ACCORDION_SUMMARY}
      >
        <Typography>{props.title}</Typography>
        {hasSelectedRefiners && <FilterFlag />}
      </AccordionSummary>

      {/* Show loading spinnner if it is on initializing */}
      {isInitializing && (
        <AccordionDetails>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={20} />
          </Box>
        </AccordionDetails>
      )}

      {/* Show message if site is not selected */}
      {!isAnySiteSelected && (
        <AccordionDetails sx={{ textAlign: 'center' }}>
          <Typography>...</Typography>
        </AccordionDetails>
      )}

      {/* Show message if empty */}
      {isLocationSelected &&
        isAnySiteSelected &&
        !isInitializing &&
        (!items || items.length === 0) && (
          <AccordionDetails sx={{ textAlign: 'center' }}>
            <Typography>No filter options available</Typography>
          </AccordionDetails>
        )}

      {/* Show items if it's not initializing */}
      {!isInitializing && isAnySiteSelected && isLocationSelected && items && (
        <AccordionDetails {...Constants.BASE_STYLES.ACCORDION_DETAILS}>
          <FormGroup>
            {items.map((item) => (
              <FormControlLabel
                {...Constants.BASE_STYLES.FORM_CONTROL_LABEL_PROPS}
                key={item.index}
                value={item.refinerValue}
                label={item.refinerLabel}
                className={classes.checkbox}
                control={
                  <Checkbox
                    {...Constants.BASE_STYLES.FILTER_ITEM_PROPS}
                    checked={isItemChecked(props.property, item.refinerValue)}
                    onChange={(event, checked) =>
                      updateCheckedItem(item.refinerValue, checked)
                    }
                  />
                }
              />
            ))}
          </FormGroup>
        </AccordionDetails>
      )}
    </Accordion>
  );
};
